import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const ProductDataContext = createContext();

const PRODUCT_INDEX_URL = "/data/products/index.v1.json";
const CATEGORIES_URL = "/data/products/categories.v1.json";
const PRODUCT_DETAIL_BASE_URL = "/data/products/details";
const ATTRIBUTE_CONFIG_URL = "/data/config/product_attributes.json";
const ATTRIBUTE_PROFILES_URL = "/data/config/category_attribute_profiles.json";
const SCHEMA_LABELS_URL = "/data/config/schema_labels.json";

const EMPTY_COMPAT = {
  protocols: [],
  standards: [],
  ecosystems: [],
  controlMethods: [],
  requiresHubs: [],
  compatibleHubs: [],
  directControl: { available: false, methods: [] },
};

const EMPTY_STOCK = {
  inStock: false,
  quantity: 0,
  leadTimeDays: 0,
  allowBackorder: false,
};

const PLACEHOLDER_IMAGE = "/img/placeholder.webp";

function asArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === undefined || value === null || value === "") return [];
  return [value];
}

function unique(values) {
  return Array.from(new Set(asArray(values).map(String).map((v) => v.trim()).filter(Boolean)));
}

function normalizeCompat(compat = {}) {
  const directControl = compat.directControl || {};
  return {
    protocols: unique(compat.protocols),
    standards: unique(compat.standards),
    ecosystems: unique(compat.ecosystems),
    controlMethods: unique(compat.controlMethods),
    requiresHubs: unique(compat.requiresHubs),
    compatibleHubs: unique(compat.compatibleHubs),
    directControl: {
      available: Boolean(directControl.available),
      methods: Array.isArray(directControl.methods) ? directControl.methods : [],
    },
  };
}

function normalizeStock(stock = {}) {
  return {
    inStock: Boolean(stock.inStock),
    quantity: Number.isFinite(Number(stock.quantity)) ? Number(stock.quantity) : 0,
    leadTimeDays: Number.isFinite(Number(stock.leadTimeDays)) ? Number(stock.leadTimeDays) : 0,
    allowBackorder: Boolean(stock.allowBackorder),
  };
}

function pickImage(product = {}) {
  if (product.image) {
    return {
      url: product.image,
      alt: product.imageAlt || `${product.name || "Termék"} – termékkép`,
    };
  }

  const images = product.media?.images;
  if (Array.isArray(images) && images.length) {
    const primary = images.find((img) => img?.isPrimary) || images[0];
    return {
      url: primary?.url || PLACEHOLDER_IMAGE,
      alt: primary?.alt || `${product.name || "Termék"} – termékkép`,
    };
  }

  return { url: PLACEHOLDER_IMAGE, alt: product.name || "Termékkép" };
}

/** v1 index/detail → egységes termék objektum (nincs legacy spread). */
export function normalizeCatalogProduct(raw = {}) {
  const image = pickImage(raw);
  const categoryId = typeof raw.categoryId === "string" ? raw.categoryId : "";
  const subCategoryIds = unique(raw.subCategoryIds);
  const categoryIds = unique(
    Array.isArray(raw.categoryIds) && raw.categoryIds.length
      ? raw.categoryIds
      : [categoryId, ...subCategoryIds]
  );

  const normalized = {
    id: raw.id || raw.slug || "",
    slug: raw.slug || raw.id || "",
    name: raw.name || "",
    subtitle: raw.subtitle || "",
    brand: raw.brand || "",
    price: Number.isFinite(Number(raw.price)) ? Number(raw.price) : 0,
    categoryId: categoryId || categoryIds[0] || "",
    subCategoryIds,
    categoryIds,
    functionIds: unique(raw.functionIds),
    tags: unique(raw.tags),
    capabilities: unique(raw.capabilities),
    apps: unique(raw.apps),
    facets: raw.facets && typeof raw.facets === "object" ? raw.facets : {},
    image: image.url,
    imageAlt: image.alt,
    stock: normalizeStock(raw.stock || EMPTY_STOCK),
    compat: normalizeCompat(raw.compat || EMPTY_COMPAT),
    seo: {
      metaTitle: raw.seo?.metaTitle || raw.name || "",
      metaDescription: raw.seo?.metaDescription || raw.subtitle || "",
    },
  };

  // Detail-only mezők — csak ha jelen vannak.
  if (typeof raw.description === "string") normalized.description = raw.description;
  if (raw.media && typeof raw.media === "object") normalized.media = raw.media;
  if (raw.specs && typeof raw.specs === "object") normalized.specs = raw.specs;
  if (raw.identifiers && typeof raw.identifiers === "object") normalized.identifiers = raw.identifiers;
  if (raw.warranty && typeof raw.warranty === "object") normalized.warranty = raw.warranty;
  if (raw.related && typeof raw.related === "object") normalized.related = raw.related;

  return normalized;
}

export function productImageUrl(product) {
  return product?.image || PLACEHOLDER_IMAGE;
}

export function productImageAlt(product) {
  return product?.imageAlt || product?.name || "Termékkép";
}

export function productInStock(product) {
  return Boolean(product?.stock?.inStock);
}

function flattenCategories(categories = []) {
  const out = [];
  const walk = (items = [], parentId = null, depth = 0) => {
    for (const c of items) {
      const children = Array.isArray(c.children) ? c.children : [];
      out.push({ id: c.id, name: c.name, image: c.image, parentId, depth, isLeaf: children.length === 0 });
      walk(children, c.id, depth + 1);
    }
  };
  walk(categories);
  return out;
}

async function fetchJson(url, fallback) {
  try {
    // Versionált *.v1.json / config: böngésző cache OK (sync után hard refresh / új fájlnevek).
    const response = await fetch(url);
    if (!response.ok) return fallback;
    return await response.json();
  } catch (error) {
    console.error(`JSON fetch error: ${url}`, error);
    return fallback;
  }
}

function productDetailUrl(productId) {
  return `${PRODUCT_DETAIL_BASE_URL}/${encodeURIComponent(productId)}.v1.json`;
}

export function ProductDataProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [attributeConfig, setAttributeConfig] = useState({ attributes: {}, filterGroups: {}, sections: {} });
  const [attributeProfiles, setAttributeProfiles] = useState({ global: {}, profiles: {}, assignments: [] });
  const [schemaLabels, setSchemaLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const detailCacheRef = useRef(new Map());

  const byId = useMemo(() => {
    const map = new Map();
    for (const product of products) {
      if (product.id) map.set(product.id, product);
      if (product.slug) map.set(product.slug, product);
    }
    return map;
  }, [products]);

  const productsByCategoryId = useMemo(() => {
    const map = new Map();
    for (const product of products) {
      for (const categoryId of product.categoryIds || []) {
        if (!map.has(categoryId)) map.set(categoryId, []);
        map.get(categoryId).push(product);
      }
    }
    return map;
  }, [products]);

  const productsByFunctionId = useMemo(() => {
    const map = new Map();
    for (const product of products) {
      for (const functionId of product.functionIds || []) {
        if (!map.has(functionId)) map.set(functionId, []);
        map.get(functionId).push(product);
      }
    }
    return map;
  }, [products]);

  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);

  useEffect(() => {
    let alive = true;

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [categoriesJson, catalogJson, attributesJson, profilesJson, labelsJson] =
          await Promise.all([
            fetchJson(CATEGORIES_URL, null),
            fetchJson(PRODUCT_INDEX_URL, null),
            fetchJson(ATTRIBUTE_CONFIG_URL, { attributes: {}, filterGroups: {}, sections: {} }),
            fetchJson(ATTRIBUTE_PROFILES_URL, { global: {}, profiles: {}, assignments: [] }),
            fetchJson(SCHEMA_LABELS_URL, {}),
          ]);
        if (!alive) return;

        if (!catalogJson || !Array.isArray(catalogJson.products)) {
          throw new Error(`Érvénytelen termékindex: ${PRODUCT_INDEX_URL}`);
        }
        if (!categoriesJson || !Array.isArray(categoriesJson.categories)) {
          throw new Error(`Érvénytelen kategóriafa: ${CATEGORIES_URL}`);
        }

        detailCacheRef.current.clear();
        setProducts(catalogJson.products.map(normalizeCatalogProduct));
        setCategories(categoriesJson.categories);
        setAttributeConfig(attributesJson && typeof attributesJson === "object" ? attributesJson : {});
        setAttributeProfiles(profilesJson && typeof profilesJson === "object" ? profilesJson : {});
        setSchemaLabels(labelsJson && typeof labelsJson === "object" ? labelsJson : {});
      } catch (err) {
        if (!alive) return;
        console.error("Product data loading failed", err);
        setError(err);
        setProducts([]);
        setCategories([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadData();
    return () => {
      alive = false;
    };
  }, []);

  const loadFullProduct = useCallback(
    async (productOrId) => {
      const summary =
        typeof productOrId === "string" ? byId.get(productOrId) || null : productOrId || null;

      const detailId =
        (summary && summary.id) ||
        (typeof productOrId === "string" && byId.get(productOrId)?.id) ||
        (typeof productOrId === "string" ? productOrId : null);

      if (!detailId) return summary;

      if (detailCacheRef.current.has(detailId)) {
        return detailCacheRef.current.get(detailId);
      }

      const detailJson = await fetchJson(productDetailUrl(detailId), null);
      const full = detailJson?.product && typeof detailJson.product === "object" ? detailJson.product : null;
      if (!full) return summary;

      const merged = normalizeCatalogProduct(summary ? { ...summary, ...full } : full);
      detailCacheRef.current.set(detailId, merged);
      return merged;
    },
    [byId]
  );

  const value = useMemo(
    () => ({
      products,
      categories,
      attributeConfig,
      attributeProfiles,
      schemaLabels,
      loading,
      error,
      byId,
      productsByCategoryId,
      productsByFunctionId,
      flatCategories,
      loadFullProduct,
    }),
    [
      products,
      categories,
      attributeConfig,
      attributeProfiles,
      schemaLabels,
      loading,
      error,
      byId,
      productsByCategoryId,
      productsByFunctionId,
      flatCategories,
      loadFullProduct,
    ]
  );

  return <ProductDataContext.Provider value={value}>{children}</ProductDataContext.Provider>;
}

export const useProductData = () => useContext(ProductDataContext);
